import React, { useState, useEffect, useRef } from "react";
import styles from "./spaceinvader.module.css";
import "bootstrap/dist/css/bootstrap.min.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import { userGameRewards } from "../../../apis/user";
import { logPageView } from "../../../components/analytics";
import { useAudio } from "../../../Providers/AudioProvider";
import task from "../Task";

// Particle class for background effect
class Particle {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.x = Math.random() * canvas.width;
    this.y = Math.random() * canvas.height;
    this.size = Math.random() * 2 + 0.5;
    this.speed = Math.random() * 0.2 + 0.05;
    this.opacity = Math.random() * 0.2 + 0.1;
    this.color = `hsl(${Math.random() * 360}, 70%, 60%)`;
    this.trail = [];
    this.maxTrailLength = 5;
  }

  update() {
    // Add current position to trail
    this.trail.push({ x: this.x, y: this.y });
    if (this.trail.length > this.maxTrailLength) {
      this.trail.shift();
    }

    this.y -= this.speed;
    if (this.y < -10) {
      this.y = this.canvas.height + 10;
      this.x = Math.random() * this.canvas.width;
      this.trail = [];
    }
  }

  draw() {
    // Draw trail with reduced opacity
    this.trail.forEach((point, index) => {
      const alpha = (index / this.trail.length) * this.opacity * 0.1;
      this.ctx.save();
      this.ctx.globalAlpha = alpha;
      this.ctx.fillStyle = this.color;
      this.ctx.beginPath();
      this.ctx.arc(point.x, point.y, this.size * 0.5, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.restore();
    });

    // Draw main particle with reduced opacity
    this.ctx.save();
    this.ctx.globalAlpha = this.opacity * 0.1;
    this.ctx.fillStyle = this.color;
    this.ctx.beginPath();
    this.ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.restore();
  }
}

const SpaceInvader = () => {
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [showInstructions, setShowInstructions] = useState(false);
  const [playerVelocity, setPlayerVelocity] = useState(0);
  const [isMobile, setIsMobile] = useState(true);
  const [showMobileControls, setShowMobileControls] = useState(true);
  const [lastShotTime, setLastShotTime] = useState(0);
  const SHOOT_COOLDOWN = 150;
  const gameBoardRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const canvasRef = useRef(null);

  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();
  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit";
  // Game constants
  const PLAYER_SPEED = 12; // Balanced speed
  const PLAYER_ACCELERATION = 1.0; // Smooth acceleration
  const PLAYER_DECELERATION = 0.95; // Smooth deceleration
  const BULLET_SPEED = 8; // Balanced bullet speed
  const ALIEN_ROWS = 4;
  const ALIENS_PER_ROW = 9;
  const ALIEN_POINTS = 10;
  const BASE_ENEMY_SHOOT_CHANCE = 0.002; // Reduced base chance (0.2% per alien per frame)
  const ENEMY_SHOOT_CHANCE_INCREMENT = 0.001; // Increase by 0.1% per level
  const ENEMY_BULLET_SPEED = 4;
  const BASE_ENEMY_BULLET_SPEED = 3; // Slower initial bullet speed
  const ENEMY_BULLET_SPEED_INCREMENT = 0.5; // Increase speed by 0.5 per level
  const TOUCH_MOVE_THRESHOLD = 5; // Minimum distance for movement
  // Game state refs
  const gameAreaRef = useRef(null);
  const playerRef = useRef(null);
  const [aliens, setAliens] = useState([]);
  const [bullets, setBullets] = useState([]);
  const [enemyBullets, setEnemyBullets] = useState([]);
  const [playerPosition, setPlayerPosition] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [lastTouchX, setLastTouchX] = useState(null);
  const [touchVelocity, setTouchVelocity] = useState(0);
  const [alienDirection, setAlienDirection] = useState(1);
  const [alienSpeed, setAlienSpeed] = useState(2);
  const [lastAlienMove, setLastAlienMove] = useState(0);
  const ALIEN_MOVE_INTERVAL = 500;
  const ALIEN_SPEED_INCREMENT = 0.5;
  // Add a new state for level transition
  const [isLevelTransitioning, setIsLevelTransitioning] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const playClick = useAudio();

  // Toggle menu function
  const toogleMenu = () => {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: task,
      currentComponentText: "task",
      lastComponent: userDetails?.userDetails.currentComponent,
      lastComponentText: userDetails?.userDetails.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.userDetails?.menuCount + 1,
    }));
  };

  // Initialize game with normal grid formation
  const initializeGame = () => {
    if (isLevelTransitioning) return;

    const newAliens = [];
    const gameWidth = gameAreaRef.current.offsetWidth;
    const startY = 10; // Changed from 30 to 10 to start from top
    const alienWidth = 25;
    const alienHeight = 25;
    const spacing = 8;

    const totalWidth =
      ALIENS_PER_ROW * alienWidth + (ALIENS_PER_ROW - 1) * spacing;
    const startX = (gameWidth - totalWidth) / 2;

    // Create grid formation
    for (let row = 0; row < ALIEN_ROWS; row++) {
      for (let col = 0; col < ALIENS_PER_ROW; col++) {
        const x = startX + col * (alienWidth + spacing);
        const y = startY + row * (alienHeight + spacing);

        newAliens.push({
          id: `${row}-${col}`,
          x: x,
          y: y,
          type: row % 2 === 0 ? "type1" : "type2",
          lastShot: 0,
          shootCooldown: 2000 + Math.random() * 3000,
        });
      }
    }

    setAliens(newAliens);
    setBullets([]);
    setEnemyBullets([]);
    setPlayerPosition(gameWidth / 2 - 20);
    setGameOver(false);
    setGameWon(false);
    if (!gameStarted) {
      setLives(3);
    }
    setGameStarted(true);
    setAlienDirection(1);
    setLastAlienMove(Date.now());
  };

  useEffect(() => {
    // Add meta viewport tag to prevent zooming
    const viewportMeta = document.createElement("meta");
    viewportMeta.name = "viewport";
    viewportMeta.content =
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
    document.head.appendChild(viewportMeta);

    // Prevent Telegram vertical swipe behavior
    if (window.Telegram && window.Telegram.WebApp) {
      // Using the proper WebApp API method to disable vertical swipes
      // This prevents the app from collapsing when swiping inside the game area
      if (typeof window.Telegram.WebApp.disableVerticalSwipes === "function") {
        window.Telegram.WebApp.disableVerticalSwipes();
      } else {
        // Fallback for older versions that don't have the disableVerticalSwipes method
        const data = JSON.stringify({
          eventType: "web_app_setup_swipe_behavior",
          eventData: {
            allow_vertical_swipe: false,
          },
        });

        // Handle different Telegram environments
        if (window.TelegramWebviewProxy) {
          // Desktop and Mobile
          window.TelegramWebviewProxy.postEvent(
            "web_app_setup_swipe_behavior",
            JSON.stringify({ allow_vertical_swipe: false })
          );
        } else if (window.external && window.external.notify) {
          // Windows Phone
          window.external.notify(data);
        } else {
          // Web version
          window.parent.postMessage(data, "*");
        }
      }
    }

    // Prevent iOS-specific gestures that cause Telegram mini app to minimize
    const preventDefaultForTouch = (e) => {
      // Don't prevent default for buttons and interactive elements
      const target = e.target;
      const isInteractive =
        target.tagName === "BUTTON" ||
        target.closest("button") ||
        target.role === "button" ||
        target.onclick ||
        target.classList?.contains("btn") ||
        target.classList?.contains("quest") ||
        target.closest(".quest") ||
        target.closest(".control_btn") ||
        target.closest(".overlay") ||
        target.closest(".quento_full_popup");

      if (isInteractive) {
        return; // Allow interactive elements to work
      }

      // Prevent default for game board elements
      if (
        gameBoardRef.current &&
        (gameBoardRef.current.contains(target) ||
          target === gameBoardRef.current)
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Add event listeners with passive: false to ensure preventDefault works
    document.addEventListener("touchstart", preventDefaultForTouch, {
      passive: false,
    });
    document.addEventListener("touchmove", preventDefaultForTouch, {
      passive: false,
    });
    document.addEventListener("touchend", preventDefaultForTouch, {
      passive: false,
    });

    // Add specific iOS body styling to prevent bouncing/scrolling
    document.body.style.overscrollBehavior = "none";
    document.body.style.overflow = "hidden";

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(viewportMeta);
      document.removeEventListener("touchstart", preventDefaultForTouch);
      document.removeEventListener("touchmove", preventDefaultForTouch);
      document.removeEventListener("touchend", preventDefaultForTouch);
      document.body.style.overscrollBehavior = "";
      document.body.style.overflow = "";
    };
  }, []);

  // Remove continuous shooting effect
  useEffect(() => {
    if (!gameStarted || gameOver || gameWon) return;

    const gameArea = gameAreaRef.current;
    const player = playerRef.current;
    if (!gameArea || !player) return;

    const moveInterval = setInterval(() => {
      setPlayerPosition((prev) => {
        const newPosition = prev + playerVelocity;
        const gameAreaWidth = gameArea.offsetWidth;
        const playerWidth = player.offsetWidth;

        // Smooth boundary response
        if (newPosition < 0) {
          setPlayerVelocity(0);
          return 0;
        }
        if (newPosition > gameAreaWidth - playerWidth) {
          setPlayerVelocity(0);
          return gameAreaWidth - playerWidth;
        }
        return newPosition;
      });

      // Smooth deceleration
      setPlayerVelocity((prev) => {
        if (Math.abs(prev) < 0.1) return 0;
        return prev * PLAYER_DECELERATION;
      });
    }, 16);

    return () => clearInterval(moveInterval);
  }, [gameStarted, gameOver, gameWon, playerVelocity]);

  // Optimize touch handlers for better reactivity
  const handleTouchStart = (e) => {
    if (!gameStarted || gameOver || gameWon) return;
    e.preventDefault();
    const touch = e.touches[0];
    setTouchStartX(touch.clientX);
    setLastTouchX(touch.clientX);
    setIsTouching(true);
    handleShoot(); // Initial shot on touch
  };

  const handleTouchMove = (e) => {
    if (!gameStarted || gameOver || gameWon || !isTouching) return;
    e.preventDefault();
    const touch = e.touches[0];
    const gameArea = gameAreaRef.current;
    const player = playerRef.current;
    if (!gameArea || !player) return;

    const touchDiff = touch.clientX - lastTouchX;

    // Only move if the touch movement is significant enough
    if (Math.abs(touchDiff) > TOUCH_MOVE_THRESHOLD) {
      const newPosition = playerPosition + touchDiff * 1.5;
      const gameAreaWidth = gameArea.offsetWidth;
      const playerWidth = player.offsetWidth;

      setPlayerPosition(
        Math.max(0, Math.min(gameAreaWidth - playerWidth, newPosition))
      );
      setLastTouchX(touch.clientX);
    }
  };

  const handleTouchEnd = (e) => {
    if (e) e.preventDefault();
    setTouchStartX(null);
    setLastTouchX(null);
    setIsTouching(false);
    setPlayerVelocity(0);
  };

  // Optimize keyboard handler for tap-to-shoot
  const handleKeyDown = (e) => {
    if (!gameStarted || gameOver || gameWon) return;

    const gameArea = gameAreaRef.current;
    const player = playerRef.current;
    if (!gameArea || !player) return;

    if (e.key === "ArrowLeft") {
      setPlayerVelocity((prev) =>
        Math.max(prev - PLAYER_ACCELERATION, -PLAYER_SPEED)
      );
    } else if (e.key === "ArrowRight") {
      setPlayerVelocity((prev) =>
        Math.min(prev + PLAYER_ACCELERATION, PLAYER_SPEED)
      );
    } else if (e.key === " ") {
      handleShoot(); // Shoot only on spacebar press
    }
  };

  // Add key up handler for movement control
  const handleKeyUp = (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      setPlayerVelocity(0);
    }
  };

  // Add keyboard event listeners
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [gameStarted, gameOver, gameWon, playerPosition]);

  // Optimize shooting mechanics
  const handleShoot = () => {
    if (!gameStarted || gameOver || gameWon) return;
    const now = Date.now();

    // Check if enough time has passed since last shot
    if (now - lastShotTime < SHOOT_COOLDOWN) return;

    const player = playerRef.current;
    if (!player) return;

    const newBullet = {
      id: Date.now(),
      x: playerPosition + player.offsetWidth / 2,
      y: gameAreaRef.current.offsetHeight - player.offsetHeight,
    };
    setBullets((prev) => [...prev, newBullet]);
    setLastShotTime(now);
  };

  // Calculate current level's shooting chance and bullet speed
  const getCurrentLevelStats = () => {
    const shootChance =
      BASE_ENEMY_SHOOT_CHANCE + ENEMY_SHOOT_CHANCE_INCREMENT * (level - 1);
    const bulletSpeed =
      BASE_ENEMY_BULLET_SPEED + ENEMY_BULLET_SPEED_INCREMENT * (level - 1);
    return { shootChance, bulletSpeed };
  };

  // Handle enemy shooting with level-based difficulty
  const handleEnemyShooting = () => {
    const now = Date.now();
    const newEnemyBullets = [];
    const { shootChance, bulletSpeed } = getCurrentLevelStats();

    aliens.forEach((alien) => {
      if (
        now - alien.lastShot >= alien.shootCooldown &&
        Math.random() < shootChance
      ) {
        newEnemyBullets.push({
          id: Date.now() + Math.random(),
          x: alien.x + 20,
          y: alien.y + 40,
          speed: bulletSpeed, // Add speed property to bullets
        });
        alien.lastShot = now;
      }
    });

    if (newEnemyBullets.length > 0) {
      setEnemyBullets((prevBullets) => [...prevBullets, ...newEnemyBullets]);
    }
  };

  // Handle API response
  const handleApiResponse = async (points) => {
    if (points <= 0) {
      if (gameWon) {
        setGameWon(true);
      } else {
        setGameOver(true);
      }
      return;
    }

    try {
      const apiData = {
        gamePoints: points,
        gameType: "spaceinvader",
      };
      const res = await userGameRewards(apiData);

      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));

        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  // Optimize bullet movement
  useEffect(() => {
    if (!gameStarted || gameOver || gameWon) return;

    const gameLoop = setInterval(() => {
      // Move bullets with improved performance
      setBullets((prevBullets) =>
        prevBullets
          .map((bullet) => ({
            ...bullet,
            y: bullet.y - BULLET_SPEED,
          }))
          .filter((bullet) => bullet.y > 0)
      );

      // Move enemy bullets with improved performance
      setEnemyBullets((prevBullets) =>
        prevBullets
          .map((bullet) => ({
            ...bullet,
            y: bullet.y + (bullet.speed || ENEMY_BULLET_SPEED),
          }))
          .filter((bullet) => bullet.y < gameAreaRef.current.offsetHeight)
      );
    }, 16); // 60fps update rate

    return () => clearInterval(gameLoop);
  }, [gameStarted, gameOver, gameWon]);

  // Modify the game loop collision checks
  useEffect(() => {
    if (!gameStarted || gameOver || gameWon) return;

    const gameLoop = setInterval(() => {
      const now = Date.now();

      // Move bullets
      setBullets((prevBullets) =>
        prevBullets
          .map((bullet) => ({
            ...bullet,
            y: bullet.y - BULLET_SPEED,
          }))
          .filter((bullet) => bullet.y > 0)
      );

      // Move enemy bullets with individual speeds
      setEnemyBullets((prevBullets) =>
        prevBullets
          .map((bullet) => ({
            ...bullet,
            y: bullet.y + (bullet.speed || ENEMY_BULLET_SPEED),
          }))
          .filter((bullet) => bullet.y < gameAreaRef.current.offsetHeight)
      );

      // Handle enemy shooting
      handleEnemyShooting();

      // Move aliens
      if (now - lastAlienMove >= ALIEN_MOVE_INTERVAL) {
        setAliens((prevAliens) => {
          const newAliens = [...prevAliens];
          let shouldChangeDirection = false;
          const gameWidth = gameAreaRef.current.offsetWidth;
          const alienWidth = 40;

          // Check if any alien hits the boundaries
          newAliens.forEach((alien) => {
            if (
              (alienDirection === 1 && alien.x + alienWidth >= gameWidth) ||
              (alienDirection === -1 && alien.x <= 0)
            ) {
              shouldChangeDirection = true;
            }
          });

          // Move all aliens together
          newAliens.forEach((alien) => {
            if (shouldChangeDirection) {
              alien.y += 20; // Move down when changing direction
            }
            alien.x += alienSpeed * alienDirection;
          });

          // Check if aliens have reached the bottom
          if (
            newAliens.some(
              (alien) =>
                alien.y + alienWidth >= gameAreaRef.current.offsetHeight - 100 // Increased margin from bottom
            )
          ) {
            setGameOver(true);
            handleApiResponse(score);
            return newAliens;
          }

          if (shouldChangeDirection) {
            setAlienDirection((prev) => -prev);
          }

          setLastAlienMove(now);
          return newAliens;
        });
      }

      // Check collisions
      setBullets((prevBullets) => {
        const newBullets = [...prevBullets];
        setAliens((prevAliens) => {
          const newAliens = [...prevAliens];
          let hitAlien = false;

          for (let i = newBullets.length - 1; i >= 0; i--) {
            const bullet = newBullets[i];
            for (let j = newAliens.length - 1; j >= 0; j--) {
              const alien = newAliens[j];
              if (
                bullet.x >= alien.x &&
                bullet.x <= alien.x + 40 &&
                bullet.y >= alien.y &&
                bullet.y <= alien.y + 40
              ) {
                newBullets.splice(i, 1);
                newAliens.splice(j, 1);
                setScore((prev) => prev + ALIEN_POINTS);
                hitAlien = true;
                break;
              }
            }
            if (hitAlien) break;
          }

          if (newAliens.length === 0 && !isLevelTransitioning) {
            // Level completed - prepare for next level
            handleLevelTransition();
          }
          return newAliens;
        });
        return newBullets;
      });

      // Modify the player collision check
      setEnemyBullets((prevBullets) => {
        const newBullets = [...prevBullets];
        const player = playerRef.current;

        if (player) {
          for (let i = newBullets.length - 1; i >= 0; i--) {
            const bullet = newBullets[i];
            if (
              bullet.x >= playerPosition &&
              bullet.x <= playerPosition + player.offsetWidth &&
              bullet.y >=
                gameAreaRef.current.offsetHeight - player.offsetHeight - 20 &&
              bullet.y <= gameAreaRef.current.offsetHeight - 20
            ) {
              newBullets.splice(i, 1);
              setLives((prev) => {
                const newLives = Math.max(0, prev - 1);
                if (newLives === 0) {
                  setGameOver(true);
                  handleApiResponse(score);
                }
                return newLives;
              });
              break;
            }
          }
        }
        return newBullets;
      });
    }, 16);

    return () => clearInterval(gameLoop);
  }, [
    gameStarted,
    gameOver,
    gameWon,
    alienDirection,
    lastAlienMove,
    alienSpeed,
    playerPosition,
    level,
  ]);

  // Add analytics logging
  useEffect(() => {
    logPageView("spaceinvader", "spaceinvader");
  }, []);

  // Add a new function to handle game reset
  const handlePlayAgain = () => {
    setGameOver(false);
    setGameWon(false);
    setScore(0);
    setLives(3);
    setLevel(1);
    setAlienSpeed(2);
    setGameStarted(false);
    setAliens([]);
    setBullets([]);
    setEnemyBullets([]);
    setPlayerPosition(0);
    setAlienDirection(1);
    setLastAlienMove(Date.now());
    setIsLevelTransitioning(false);
  };

  // Add effect to hide mobile controls after 3 seconds
  useEffect(() => {
    if (isMobile && showMobileControls) {
      const timer = setTimeout(() => {
        setShowMobileControls(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isMobile, showMobileControls]);

  // Modify level transition to show difficulty increase
  const handleLevelTransition = () => {
    setIsLevelTransitioning(true);
    const nextLevel = level + 1;
    const newSpeed = alienSpeed + ALIEN_SPEED_INCREMENT;
    const { shootChance, bulletSpeed } = getCurrentLevelStats();

    setLevel(nextLevel);
    setAlienSpeed(newSpeed);
    setScore((prev) => prev + 100);

    setTimeout(() => {
      setIsLevelTransitioning(false);
      initializeGame();
    }, 2000); // Increased delay to show level transition
  };

  // Update the level transition UI to show difficulty increase
  const renderLevelTransition = () => {
    if (!isLevelTransitioning) return null;
    const { shootChance, bulletSpeed } = getCurrentLevelStats();
    const completedLevel = level - 1; // Show the level that was just completed

    return (
      <div className={styles.levelTransition}>
        <h2>Level {completedLevel} Completed!</h2>
        <div className={styles.levelStats}>
          <p className={styles.completedText}>
            You've cleared Level {completedLevel}!
          </p>
          <p className={styles.nextLevelText}>Get ready for Level {level}</p>
          <div className={styles.difficultyIncrease}>
            <p>Difficulty Increased:</p>
            <p>• Alien Shooting Speed: {Math.round(shootChance * 1000)}%</p>
            <p>• Bullet Speed: {Math.round(bulletSpeed * 10) / 10}</p>
          </div>
        </div>
      </div>
    );
  };

  // Add touch event listeners with better options
  useEffect(() => {
    const gameArea = gameAreaRef.current;
    if (!gameArea) return;

    const options = {
      passive: false,
      capture: true,
    };

    gameArea.addEventListener("touchstart", handleTouchStart, options);
    gameArea.addEventListener("touchmove", handleTouchMove, options);
    gameArea.addEventListener("touchend", handleTouchEnd, options);
    gameArea.addEventListener("touchcancel", handleTouchEnd, options);

    return () => {
      gameArea.removeEventListener("touchstart", handleTouchStart, options);
      gameArea.removeEventListener("touchmove", handleTouchMove, options);
      gameArea.removeEventListener("touchend", handleTouchEnd, options);
      gameArea.removeEventListener("touchcancel", handleTouchEnd, options);
    };
  }, [
    gameStarted,
    gameOver,
    gameWon,
    playerPosition,
    isTouching,
    lastShotTime,
  ]);

  // Add a separate click handler for the instructions button
  const handleInstructionsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowInstructions(true);
  };

  // Add a separate click handler for the start game button
  const handleStartGame = (e) => {
    e.preventDefault();
    e.stopPropagation();
    initializeGame();
  };

  // Initialize particles
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext("2d");

    // Create particles
    const newParticles = [];
    for (let i = 0; i < 100; i++) {
      newParticles.push(new Particle(canvas));
    }
    setParticles(newParticles);

    // Animation loop for particles
    const animate = () => {
      ctx.fillStyle = "rgba(10, 10, 10, 0.1)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      newParticles.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      requestAnimationFrame(animate);
    };

    animate();

    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className={styles.gameContainer}>
      <canvas
        ref={canvasRef}
        className={styles.backgroundCanvas}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <div
        ref={gameBoardRef}
        className={styles.container}
        style={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          margin: "0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "5px", width: "100%" }}>
          <img
            onClick={() => {
              toogleMenu(task, "task");
            }}
            src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
            className="cancel-full"
            style={{ cursor: "pointer" }}
          />
          <h3
            className={styles.welcome_text}
            style={{ fontSize: "1.2em", margin: "0 0 5px 0" }}
          >
            SPACE INVADER
          </h3>

          <div
            className={styles.infoGrid}
            style={{
              fontSize: "0.9em",
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              textAlign: "center",
            }}
          >
            <div className={styles.infoCol}>
              <span className={styles.infoLabel}>Level:{level}</span>
            </div>
            <div className={styles.infoCol}>
              <span className={styles.infoLabel}>Score:{score}</span>
              <span className={styles.infoValue}></span>
            </div>
            <div className={styles.infoCol}>
              <span className={styles.infoLabel}>
                Lives:
                <span
                  className={styles.infoValue}
                  style={{ fontSize: "0.8em" }}
                >
                  {Array(Math.max(0, lives)).fill("❤️").join("")}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div
          className={styles.gameArea}
          ref={gameAreaRef}
          style={{
            flex: 1,
            width: "100%",
            position: "relative",
            overflow: "hidden",
            margin: "0",
            touchAction: "none",
            maxWidth: "100%",
          }}
        >
          {!gameStarted ? (
            <div className={styles.startScreen}>
              <button
                className={styles.control_btn}
                onClick={handleStartGame}
                onTouchEnd={handleStartGame}
              >
                Start Game
              </button>
            </div>
          ) : (
            <>
              {isLevelTransitioning && renderLevelTransition()}
              <div
                className={styles.player}
                ref={playerRef}
                style={{ left: `${playerPosition}px` }}
              >
                <img
                  src="https://d1ccopf5cw1hft.cloudfront.net/s3data/spaceship.png"
                  alt="Player"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
              {aliens.map((alien) => (
                <div
                  key={alien.id}
                  className={`${styles.alien} ${styles[alien.type]}`}
                  style={{ left: `${alien.x}px`, top: `${alien.y}px` }}
                >
                  <img
                    src={
                      alien.type === "type1"
                        ? "https://d1ccopf5cw1hft.cloudfront.net/s3data/alien-1.png"
                        : "https://d1ccopf5cw1hft.cloudfront.net/s3data/alien.png"
                    }
                    alt={`Alien ${alien.type}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              ))}
              {bullets.map((bullet) => (
                <div
                  key={bullet.id}
                  className={styles.bullet}
                  style={{ left: `${bullet.x}px`, top: `${bullet.y}px` }}
                />
              ))}
              {enemyBullets.map((bullet) => (
                <div
                  key={bullet.id}
                  className={styles.enemyBullet}
                  style={{ left: `${bullet.x}px`, top: `${bullet.y}px` }}
                />
              ))}
              {showMobileControls && (
                <div className={styles.mobileControls}>
                  <div className={styles.mobileInstructions}>
                    <p>Swipe to move • Tap to shoot</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <p
          className={styles.quest}
          onClick={handleInstructionsClick}
          onTouchEnd={handleInstructionsClick}
          title="How to Play"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
          </svg>
        </p>

        {gameOver && (
          <div className={styles.overlay}>
            <div className={styles.quento_full_popup}>
              <div className={styles.quento_popup_content}>
                <h2 className={styles.quento_epic}>Game Over!</h2>
                <img
                  src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                  className={styles.quento_cancel_img}
                  onClick={handlePlayAgain}
                  onTouchEnd={handlePlayAgain}
                />
                <div className={styles.quento_epic_div}>
                  <p className={styles.quento_pointsLabel}>SCORE</p>
                  <div className={styles.quento_pointsValue1}>
                    <img
                      src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                      alt="coin"
                    />
                    <p className={styles.quento_pointsNumber}>{score}</p>
                  </div>
                </div>
                <p className={styles.quento_bottom_text}>
                  Keep playing! Challenge yourself to beat your high score!
                </p>
                <button
                  className={styles.control_btn}
                  onClick={handlePlayAgain}
                  onTouchEnd={handlePlayAgain}
                >
                  Play Again
                </button>
              </div>
            </div>
          </div>
        )}

        {gameWon && (
          <div className={styles.overlay}>
            <div className={styles.quento_full_popup}>
              <div className={styles.quento_popup_content}>
                <h2 className={styles.quento_epic}>Congratulations!</h2>
                <img
                  src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                  className={styles.quento_cancel_img}
                  onClick={handlePlayAgain}
                />
                {!IsPointsReached ? (
                  <div className={styles.quento_epic_div}>
                    <p className={styles.quento_pointsLabel}>TOKENS EARNED</p>
                    <div className={styles.quento_pointsValue1}>
                      <img
                        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                        alt="coin"
                      />
                      <p className={styles.quento_pointsNumber}>{score}</p>
                    </div>
                  </div>
                ) : (
                  <div className={styles.quento_epic_div}>
                    <p className={styles.quento_pointsLabel}>
                      POINTS EARNED: 0 <br />
                      TOKENS EARNED: NIL
                    </p>
                  </div>
                )}
                <p className={styles.quento_bottom_text}>
                  Amazing job! You've cleared all the aliens!
                </p>
                <button
                  className={styles.control_btn}
                  onClick={handlePlayAgain}
                >
                  Play Again
                </button>
              </div>
            </div>
          </div>
        )}

        {showInstructions && (
          <div className={styles.overlay}>
            <div className={styles.quento_full_popup}>
              <div className={styles.quento_popup_content}>
                <h2 className={styles.quento_epic}>How to Play</h2>
                <img
                  src="https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png"
                  className={styles.quento_cancel_img}
                  onClick={() => setShowInstructions(false)}
                  onTouchEnd={() => setShowInstructions(false)}
                />
                <div className={styles.quento_epic_div}>
                  <div className={styles.instruction_item}>
                    <div className={styles.instruction_icon}>⬅️ ➡️</div>
                    <p className={styles.instruction_text}>
                      Use left and right arrow keys to move
                    </p>
                  </div>
                  <div className={styles.instruction_item}>
                    <div className={styles.instruction_icon}>⏎</div>
                    <p className={styles.instruction_text}>
                      Press spacebar to shoot
                    </p>
                  </div>
                  <div className={styles.instruction_item}>
                    <div className={styles.instruction_icon}>👾</div>
                    <p className={styles.instruction_text}>
                      Destroy all aliens to win
                    </p>
                  </div>
                  <div className={styles.instruction_item}>
                    <div className={styles.instruction_icon}>❤️</div>
                    <p className={styles.instruction_text}>You have 3 lives</p>
                  </div>
                  <div className={styles.instruction_item}>
                    <div className={styles.instruction_icon}>🎯</div>
                    <p className={styles.instruction_text}>
                      Different aliens give different points
                    </p>
                  </div>
                  <div className={styles.instruction_item}>
                    <div className={styles.instruction_icon}>🏆</div>
                    <p className={styles.instruction_text}>
                      Try to beat your high score!
                    </p>
                  </div>
                </div>
                <button
                  className={styles.control_btn}
                  onClick={() => setShowInstructions(false)}
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpaceInvader;
